/*
 * The sign-in screen.
 *
 * One account, no registration, no reset link — the credential is set on the
 * Worker with `npm run auth:setup`. So this is deliberately not a "welcome"
 * page: there is nothing to sign up for and nobody to recover an account for.
 * It says whose dashboard this is, takes two fields, and gets out of the way.
 *
 * The submit is genuinely slow (600k PBKDF2 rounds run in this tab — see
 * data/auth.ts), which is why the button has a real pending state rather than
 * an optimistic one. Half a second of "Signing in…" is the honest reading of
 * what the machine is doing.
 */

import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { AuthError, login, type Session } from '../../data/auth';
import { IconEye, IconEyeOff, IconLock, IconMoon, IconSun, IconUser } from '../Icons';
import logoUrl from '../../assets/pyraxis-logo.png';

interface Props {
  onSignedIn: (session: Session) => void;
  /**
   * True when a stored session was refused, rather than never having existed.
   * Worth distinguishing: "signed out" is something the person did, and
   * "expired" is something that happened to them while they were away.
   */
  expired?: boolean;
  theme: 'dark' | 'light';
  onTheme: () => void;
}

export function LoginGate({ onSignedIn, expired = false, theme, onTheme }: Props) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capsLock, setCapsLock] = useState(false);
  /* Bumped on each failure so the card replays its nudge — a second wrong
     attempt has to feel different from the first, or nothing appears to have
     happened when the message does not change. */
  const [attempt, setAttempt] = useState(0);

  const userRef = useRef<HTMLInputElement>(null);
  const passRef = useRef<HTMLInputElement>(null);

  useEffect(() => userRef.current?.focus(), []);

  // Replaying the card's animation remounts it, which drops focus. After a
  // failure the caret belongs in the password field — that is the field being
  // retyped, and the alternative is a keystroke that goes nowhere.
  useEffect(() => {
    if (attempt > 0) passRef.current?.focus();
  }, [attempt]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !username.trim() || !password) return;

    setBusy(true);
    setError(null);
    try {
      onSignedIn(await login(username, password));
    } catch (err) {
      setError(err instanceof AuthError ? err.message : 'Sign-in failed. Try again.');
      setAttempt((n) => n + 1);
      // Clear only the password. Retyping a username you already got right is
      // the small indignity every bad login form inflicts.
      setPassword('');
      setBusy(false);
    }
  }

  const trackCaps = (event: KeyboardEvent<HTMLInputElement>) =>
    setCapsLock(event.getModifierState('CapsLock'));

  return (
    <div className="login">
      {/* Brand wash. Purely decorative, and kept out of the a11y tree. */}
      <div className="login__aura" aria-hidden="true" />
      <div className="login__mesh" aria-hidden="true" />

      <button
        type="button"
        className="icon-btn login__theme"
        onClick={onTheme}
        aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
      >
        {theme === 'dark' ? <IconSun size={15} /> : <IconMoon size={15} />}
      </button>

      <main className="login__card" key={attempt} data-shake={attempt > 0 ? '' : undefined}>
        {/* A chart line, drawn once as the card appears. This is an analytics
            product; the sign-in screen may as well say so. */}
        <svg className="login__spark" viewBox="0 0 400 40" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id="loginSpark" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="var(--accent-strong)" stopOpacity="0" />
              <stop offset="45%" stopColor="var(--accent-strong)" stopOpacity="0.9" />
              <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
            </linearGradient>
          </defs>
          <path
            d="M0 30 L40 26 L80 31 L120 18 L160 24 L200 12 L240 17 L280 8 L320 14 L360 5 L400 9"
            fill="none"
            stroke="url(#loginSpark)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <header className="login__brand">
          <img className="login__logo" src={logoUrl} alt="" />
          <div>
            <div className="login__wordmark">Pyraxis</div>
            <div className="login__wordmark-sub">Analytics</div>
          </div>
        </header>

        <h1 className="login__title">{expired ? 'Session expired' : 'Sign in'}</h1>
        <p className="login__sub">
          {expired
            ? 'You were signed out for security. Sign in again to pick up where you left off.'
            : 'Private telemetry for Pyraxis Browser — desktop and mobile.'}
        </p>

        <form onSubmit={submit} noValidate>
          <label className="login__label" htmlFor="login-user">
            Username
          </label>
          <div className="login__field">
            <IconUser size={15} />
            <input
              id="login-user"
              ref={userRef}
              className="login__input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              disabled={busy}
              required
            />
          </div>

          <label className="login__label" htmlFor="login-pass">
            Password
          </label>
          <div className="login__field">
            <IconLock size={15} />
            <input
              id="login-pass"
              ref={passRef}
              className="login__input"
              type={reveal ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyUp={trackCaps}
              onKeyDown={trackCaps}
              onBlur={() => setCapsLock(false)}
              autoComplete="current-password"
              disabled={busy}
              required
            />
            <button
              type="button"
              className="login__reveal"
              onClick={() => setReveal((v) => !v)}
              aria-label={reveal ? 'Hide password' : 'Show password'}
              title={reveal ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {reveal ? <IconEyeOff size={15} /> : <IconEye size={15} />}
            </button>
          </div>

          {/* Caps Lock is the single most common cause of a password that the
              person is certain they typed correctly. */}
          <p className="login__hint" aria-live="polite">
            {capsLock ? 'Caps Lock is on' : ' '}
          </p>

          {error ? (
            <p className="login__error" role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="login__submit" disabled={busy || !username.trim() || !password}>
            {busy ? (
              <>
                <span className="login__spinner" aria-hidden="true" />
                Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </button>
        </form>

        <p className="login__foot">
          Stays signed in on this device for 30 days. Nothing is stored anywhere else.
        </p>
      </main>
    </div>
  );
}
